/**
 * Website Rankings momentum signals (rank delta + new-entrant detection).
 *
 * The signals are derived from the backend `CategoryLeadersResponse`:
 * `RankChange` is the month-over-month change in root-category rank, and a site is
 * a new entrant when it has a current root-category rank but no prior-month rank
 * baseline. A site with no current root-category rank at all is neither a new
 * entrant nor has a computable delta. Positive `RankChange` means the site climbed
 * (a lower rank number is better).
 */

import type { ISite, IRankSignals, ITopMover, RecordWithRankSignals } from "./websiteRankings.types";

type IRankFields = { RootCategoryRank?: number | null; PrevMonthRootCategoryRank?: number | null };
type IMoverSource = IRankFields & Pick<Partial<ISite>, "Domain" | "Share">;

/**
 * Derives the momentum signals from a site's current and prior-month root-category
 * rank. A missing prior-month rank marks a new entrant (no computable delta).
 */
export const computeRankSignals = (site: IRankFields): IRankSignals => {
    const { RootCategoryRank, PrevMonthRootCategoryRank } = site;
    // Falsy check (not just null/undefined) to match RankCell's own "no rank" test
    // (dynamicFilterFilter's `!val`) — the Industry rank column shows "-" for the same values.
    if (!RootCategoryRank) {
        return { RankChange: null, IsNewEntrant: false };
    }
    if (!PrevMonthRootCategoryRank) {
        return { RankChange: null, IsNewEntrant: true };
    }
    return { RankChange: PrevMonthRootCategoryRank - RootCategoryRank, IsNewEntrant: false };
};

export const enrichRecordsWithRankSignals = <T extends IRankFields>(records: T[]): RecordWithRankSignals<T>[] =>
    records.map((record) => ({
        ...record,
        ...computeRankSignals(record),
    }));

/** Maps a backend `Site` (already resolved with its detail-page `url`) into a Top Movers row. */
export const mapSiteToMover = (site: IMoverSource, url: string): ITopMover => {
    const { RankChange, IsNewEntrant } = computeRankSignals(site);
    return {
        domain: site.Domain ?? "",
        rankChange: RankChange,
        isNewEntrant: IsNewEntrant,
        url,
        rank: site.RootCategoryRank ?? 0,
        share: site.Share ?? 0,
    };
};
